import { useState, useEffect, useRef, useCallback, type ReactNode } from "react";
import type { SiteSettings } from "@/mocks/adminData";

// 解析富文本行：支持 **红色** 和 [文字](链接) 语法
function parseRichLine(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  // 匹配 **...** 或 [文字](链接)
  const regex = /\*\*(.+?)\*\*|\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g;
  let lastIdx = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // 匹配前的普通文本（含数字自动变红）
    if (match.index > lastIdx) {
      parts.push(...autoHighlight(text.slice(lastIdx, match.index), key));
      key += 100;
    }
    if (match[1] !== undefined) {
      // **红色** 语法
      parts.push(
        <strong key={key++} className="text-red-500 font-semibold not-italic">{match[1]}</strong>
      );
    } else if (match[2] !== undefined && match[3] !== undefined) {
      // [文字](链接) 超链接语法
      parts.push(
        <a key={key++} href={match[3]} target="_blank" rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          className="text-red-500 underline font-semibold cursor-pointer hover:text-red-600 transition-colors">
          {match[2]}
        </a>
      );
    }
    lastIdx = match.index + match[0].length;
  }

  if (lastIdx < text.length) {
    parts.push(...autoHighlight(text.slice(lastIdx), key));
  }

  return parts;
}

// 数字和金额自动变红（如 1888$、500万）
function autoHighlight(text: string, baseKey: number): ReactNode[] {
  const parts: ReactNode[] = [];
  const numRegex = /(\d[\d,]*(?:\.\d+)?(?:[\$￥万亿千百%]|万+)?)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = baseKey;
  while ((m = numRegex.exec(text)) !== null) {
    if (m.index > last) {
      parts.push(<span key={k++}>{text.slice(last, m.index)}</span>);
    }
    parts.push(<span key={k++} className="text-red-500 font-semibold">{m[0]}</span>);
    last = m.index + m[0].length;
  }
  if (last < text.length) {
    parts.push(<span key={k++}>{text.slice(last)}</span>);
  }
  return parts;
}

// 渲染富文本段落（支持多段，以换行分隔）
function RichText({ text, className }: { text: string; className?: string }) {
  const lines = text.split(/\n/);
  return (
    <p className={className}>
      {lines.map((line, i) => (
        <span key={i}>
          {parseRichLine(line)}
          {i < lines.length - 1 && <br />}
        </span>
      ))}
    </p>
  );
}

interface HeroSectionProps {
  settings: SiteSettings;
}

export default function HeroSection({ settings }: HeroSectionProps) {
  const [current, setCurrent] = useState(0);
  const [loadedMap, setLoadedMap] = useState<Record<number, boolean>>({});
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const images = settings.hero_images && settings.hero_images.length > 0
    ? settings.hero_images
    : [];

  const handleImageLoad = useCallback((idx: number) => {
    setLoadedMap(prev => ({ ...prev, [idx]: true }));
  }, []);

  const aboutTitle = settings.about_title || "关于众兴";
  const defaultAbout = "众兴集团自成立以来,秉承\"合于信,盈于行\"的理念,我们始终坚信,信任是合作关系的基石,卓越的执行力则是可持续发展的引擎。在过去的发展历程中,我们始终坚守\"诚信为本、合作共赢、追求卓越\"三大核心价值观,将其作为企业立身与发展的根本。众兴的使命:持续创造价值,驱动行业变革。众兴的愿景:铸就百年品牌,成为您\"百年大业\"最信赖的合作伙伴。";
  const displayText = settings.about_text || defaultAbout;

  useEffect(() => {
    setCurrent(0);
    setLoadedMap({});
    if (timerRef.current) clearInterval(timerRef.current);
    if (images.length <= 1) return;
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % images.length);
    }, 3500);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length, images.join(",")]);

  return (
    <section id="about" className="w-full">
      {/* 背景图轮播区域 */}
      <div className="w-full relative overflow-hidden bg-gray-100" style={{ height: "clamp(160px, 22vw, 320px)" }}>
        {/* 骨架屏占位：图片未加载完时显示 */}
        {images.length === 0 || !loadedMap[current] ? (
          <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-pulse" />
        ) : null}

        {images.map((src, idx) => (
          <img
            key={src + idx}
            src={src}
            alt="众兴集团"
            onLoad={() => handleImageLoad(idx)}
            className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ${
              idx === current && loadedMap[idx] ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}

        {/* 遮罩层 */}
        {images.length > 0 && <div className="absolute inset-0 bg-black/10 pointer-events-none"></div>}

        {/* 轮播指示点（多张时显示） */}
        {images.length > 1 && (
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-10">
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrent(idx)}
                className={`rounded-full transition-all cursor-pointer ${idx === current ? "w-4 h-1.5 bg-white" : "w-1.5 h-1.5 bg-white/50"}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* 关于众兴 - 文字区 */}
      <div className="w-full bg-white px-4 md:px-10 lg:px-16 py-5 md:py-7 border-b border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-1 h-5 rounded-full bg-red-500 flex-shrink-0"></span>
            <h3 className="text-base md:text-lg font-bold text-gray-800">{aboutTitle}</h3>
          </div>
          <RichText
            text={displayText}
            className="text-sm md:text-base font-semibold text-gray-700 leading-relaxed"
          />
        </div>
      </div>
    </section>
  );
}
